import { createContext, useContext, useState, useEffect } from "react";
import apiClient from "../Api/apiConfig";

const AuthContext = createContext();

// Détecte si un user est admin à partir de plusieurs formats possibles
const computeIsAdminFromUser = (user) => {
  if (!user) return false;

  // booléen direct
  if (user.isAdmin === true) return true;

  // champ "role" en string : "admin", "ADMIN", "ROLE_ADMIN", etc.
  if (typeof user.role === "string") {
    const r = user.role.toLowerCase();
    if (r.includes("admin")) return true;
  }

  // tableau de rôles éventuel
  if (Array.isArray(user.roles)) {
    const hasAdmin = user.roles.some((r) =>
      String(r).toLowerCase().includes("admin")
    );
    if (hasAdmin) return true;
  }

  return false;
};

const normalizeUser = (raw) => {
  if (!raw) return null;
  const isAdmin = computeIsAdminFromUser(raw);
  return {
    ...raw,
    isAdmin,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // user normalisé (avec isAdmin)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const logout = () => {
    setUser(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("user");
  };

  const saveUserToStorage = (normalizedUser) => {
    // on met à jour là où il existe déjà
    if (localStorage.getItem("user")) {
      localStorage.setItem("user", JSON.stringify(normalizedUser));
    }
    if (sessionStorage.getItem("user")) {
      sessionStorage.setItem("user", JSON.stringify(normalizedUser));
    }
  };

  // Chargement user depuis storage + vérification token
  const loadUserFromStorage = async () => {
    const token =
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken");

    const storedUserRaw =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    const parsedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;

    if (token && parsedUser?.id) {
      try {
        // On récupère les infos fraîches du backend
        // ⚠️ apiClient a déjà baseURL "/api", donc PAS de "/api" ici
        const { data } = await apiClient.get(`/users/${parsedUser.id}`);

        // On fusionne : on garde les infos locales si le backend ne renvoie pas tout
        const merged = { ...parsedUser, ...data };

        const normalized = normalizeUser(merged);
        setUser(normalized);
        saveUserToStorage(normalized);
      } catch (err) {
        console.error("Erreur lors du chargement de l'utilisateur :", err);
        logout();
      }
    } else {
      logout();
    }

    setLoading(false);
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post("/users/login", {
        email,
        password,
      });

      const { user: rawUser, token } = response.data;
      const normalized = normalizeUser(rawUser);

      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("authToken", token);
      storage.setItem("user", JSON.stringify(normalized));

      setUser(normalized);
      setError(null);
    } catch (err) {
      console.error("Erreur lors de la connexion :", err);
      setError("Connexion échouée. Vérifiez vos identifiants.");
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (code, rememberMe = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post("/auth/google/callback", { code });
      const { user: rawUser, token } = response.data;

      const normalized = normalizeUser(rawUser);

      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("authToken", token);
      storage.setItem("user", JSON.stringify(normalized));

      setUser(normalized);
      setError(null);
    } catch (err) {
      console.error("Erreur lors de la connexion avec Google :", err);
      setError("Connexion avec Google échouée.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleCode = params.get("code");

    if (googleCode) {
      // On tente un login Google direct si on revient d'OAuth
      loginWithGoogle(googleCode).then(() => {
        // On nettoie l'URL pour virer le ?code=...
        window.history.replaceState({}, document.title, window.location.pathname);
      });
    } else {
      loadUserFromStorage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAuthenticated = !!user;
  const isAdmin = !!(user && user.isAdmin);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAdmin,
        login,
        loginWithGoogle,
        logout,
        error,
        loading,
      }}
    >
      {loading ? <div>Chargement...</div> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
